import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ArrowLeft02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { cx, formatPhone } from "shared/utils";
import {
  crmAnalyticsApi,
  type DateRangeParams,
  type LeadPipelineDrilldownLead,
  type LeadPipelineStage
} from "api/crm-analytics";
import { getErrorMessage } from "utils/error";
import { AnalyticsDetailModal } from "./AnalyticsDetailModal";
import { DrilldownRow } from "./DrilldownRow";
import type { UnitFilterParams } from "./UnitsFilter";
import {
  DEFAULT_PER_PAGE,
  DRILLDOWN_COLUMNS,
  DRILLDOWN_ONE_DATE_GRID_CLASSNAME,
  THREE_COLS_DRILLDOWN_GRID_CLASSNAME
} from "../constants";
import { splitStageTransitions } from "../utils";

const NOOP_PAGINATION = {
  total: 0,
  current: 1,
  onChange: () => undefined
};

type BreakdownRow = {
  key: string;
  label: string;
  title: string;
  count: number;
  pct: number;
  fromStageId: string;
  toStageId: string | null;
  fromStageName: string;
  toStageName: string | null;
};

const buildBreakdownRows = (stage: LeadPipelineStage): BreakdownRow[] => {
  const { stillInStage, movements, base } = splitStageTransitions(stage);

  const rows: BreakdownRow[] = movements.map((m) => {
    const keySuffix = m.stage_id ?? `other-${m.stage_name}`;
    return {
      key: `moved-${keySuffix}`,
      label: `${m.count} Moved into ${m.stage_name}`,
      title: `Moved into ${m.stage_name}`,
      count: m.count,
      pct: Math.round((m.count / base) * 100),
      fromStageId: stage.stage_id,
      toStageId: m.stage_id,
      fromStageName: stage.stage_name,
      toStageName: m.stage_name
    };
  });

  if (stillInStage && stillInStage.count > 0)
    rows.push({
      key: "still",
      count: stillInStage.count,
      label: `${stillInStage.count} Still in ${stage.stage_name}`,
      title: `Still in ${stage.stage_name}`,
      pct: Math.round((stillInStage.count / base) * 100),
      fromStageId: stage.stage_id,
      toStageId: stage.stage_id,
      fromStageName: stage.stage_name,
      toStageName: stage.stage_name
    });

  return rows;
};

const LEAD_STAGE_NEW = "new";
const LEAD_STAGE_SHOWING_REQUESTED = "showing requested";

type LeadDrilldownDateConfig = {
  gridClassName: string;
  columns: { label: string }[];
  getDates: (
    lead: LeadPipelineDrilldownLead
  ) => { value: string | null | undefined }[];
};

const getLeadDrilldownDateConfig = (
  rawFromStageName: string,
  rawToStageName: string | null
): LeadDrilldownDateConfig | null => {
  const fromStageName = rawFromStageName.toLowerCase();
  const toStageName = rawToStageName?.toLowerCase() ?? null;
  const isStillIn = fromStageName === toStageName;

  if (
    fromStageName === LEAD_STAGE_NEW &&
    toStageName === LEAD_STAGE_SHOWING_REQUESTED
  )
    return {
      gridClassName: DRILLDOWN_ONE_DATE_GRID_CLASSNAME,
      columns: [
        { label: "Name" },
        { label: "Property" },
        { label: "Unit" },
        { label: "Requested on" }
      ],
      getDates: (lead) => [{ value: lead.showing_requested_at }]
    };

  if (isStillIn && fromStageName === LEAD_STAGE_NEW)
    return {
      gridClassName: DRILLDOWN_ONE_DATE_GRID_CLASSNAME,
      columns: [
        { label: "Name" },
        { label: "Property" },
        { label: "Unit" },
        { label: "Date created" }
      ],
      getDates: (lead) => [{ value: lead.created_at }]
    };

  if (isStillIn && fromStageName === LEAD_STAGE_SHOWING_REQUESTED)
    return {
      gridClassName: DRILLDOWN_ONE_DATE_GRID_CLASSNAME,
      columns: [
        { label: "Name" },
        { label: "Property" },
        { label: "Unit" },
        { label: "Requested on" }
      ],
      getDates: (lead) => [{ value: lead.showing_requested_at }]
    };

  return null;
};

const BreakdownRowView = ({
  row,
  onClick
}: {
  row: BreakdownRow;
  onClick?: () => void;
}) => (
  <div
    className={cx(
      "flex items-center gap-3 px-5 py-3 bg-content-1000-inverted border-b border-alpha-40",
      onClick ? "cursor-pointer hover:bg-alpha-20" : ""
    )}
    onClick={onClick}
  >
    <p className="flex-1 text-sm font-medium text-content-700 truncate">
      {row.label}
    </p>
    <span className="text-xs font-bold text-content-1000 bg-alpha-40 px-2 py-0.5 rounded-full shrink-0 w-12 text-center">
      {row.pct}%
    </span>
  </div>
);

export const LeadPipelineModal = ({
  opened,
  onClose,
  stage,
  dateParams,
  filterParams
}: {
  opened: boolean;
  onClose: () => void;
  stage: LeadPipelineStage | null;
  dateParams: DateRangeParams;
  filterParams?: UnitFilterParams;
}) => {
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState<BreakdownRow | null>(null);
  const [page, setPage] = useState(1);

  const handleLeadClick = (leadId: number) => {
    onClose();
    navigate(`/leads/${leadId}`);
  };

  useEffect(() => {
    setPage(1);
    setSelectedRow(null);
  }, [opened, dateParams, filterParams]);

  useEffect(() => {
    setPage(1);
  }, [selectedRow]);

  const breakdownRows = stage ? buildBreakdownRows(stage) : [];

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "CRM_ANALYTICS_LEAD_PIPELINE_DRILLDOWN",
      selectedRow?.fromStageId,
      selectedRow?.toStageId,
      dateParams,
      filterParams,
      page
    ],
    queryFn: () =>
      crmAnalyticsApi.getLeadPipelineDrilldown({
        from_stage_id: selectedRow!.fromStageId,
        to_stage_id: selectedRow!.toStageId!,
        ...dateParams,
        ...filterParams,
        page,
        per_page: DEFAULT_PER_PAGE
      }),
    enabled: opened && selectedRow !== null && selectedRow.toStageId !== null,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const leads = data?.items ?? [];
  const totalPages = data?.page_count ?? 0;

  if (!stage) return null;

  const isDrillDown = selectedRow !== null;
  const title = isDrillDown ? selectedRow.title : stage.stage_name;
  const modalLoading = isDrillDown && isLoading;
  const modalFetching = isDrillDown && isFetching;

  const dateConfig =
    selectedRow && isDrillDown
      ? getLeadDrilldownDateConfig(
          selectedRow.fromStageName,
          selectedRow.toStageName
        )
      : null;
  const drilldownGridClassName =
    dateConfig?.gridClassName ?? THREE_COLS_DRILLDOWN_GRID_CLASSNAME;
  const drilldownColumns = dateConfig?.columns ?? DRILLDOWN_COLUMNS;

  return (
    <AnalyticsDetailModal
      opened={opened}
      onClose={onClose}
      title={title}
      gridClassName={drilldownGridClassName}
      columns={drilldownColumns}
      isLoading={modalLoading}
      isFetching={modalFetching}
      isEmpty={isDrillDown ? leads.length === 0 : breakdownRows.length === 0}
      emptyText="No data to show"
      pagination={
        isDrillDown
          ? { total: totalPages, current: page, onChange: setPage }
          : NOOP_PAGINATION
      }
      showColumns={isDrillDown}
      showPagination={isDrillDown}
      headerExtra={
        <Show if={isDrillDown}>
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.XS}
            HugeIcon={ArrowLeft02Icon}
            className="flex items-center gap-2"
            onClick={() => setSelectedRow(null)}
          >
            Back to breakdown
          </Button>
        </Show>
      }
      panelClassName={!isDrillDown ? "max-w-[707px]" : undefined}
    >
      <Show if={!isDrillDown}>
        <div>
          {breakdownRows.map((row) => (
            <BreakdownRowView
              key={row.key}
              row={row}
              onClick={
                row.toStageId === null ? undefined : () => setSelectedRow(row)
              }
            />
          ))}
        </div>
      </Show>
      <Show if={isDrillDown}>
        {leads.map((lead) => (
          <DrilldownRow
            key={lead.id}
            name={`${lead.first_name} ${lead.last_name}`}
            contactDetail={
              lead.primary_phone
                ? formatPhone(lead.primary_phone)
                : lead.primary_email
            }
            propertyName={lead.property?.name ?? null}
            propertyAddress={lead.property?.address.address1 ?? null}
            unitTitle={lead.unit?.title ?? null}
            onClick={() => handleLeadClick(lead.id)}
            gridClassName={dateConfig?.gridClassName}
            dates={dateConfig?.getDates(lead)}
          />
        ))}
      </Show>
    </AnalyticsDetailModal>
  );
};
